/**
 * Approve, from design/canvas/Approve.src.html and ApprovePhone.src.html: the reviewer's queue. One post
 * at a time: the render in a platform frame, the caption under the brand's voice with hashtags and the
 * below-hook, the proposed slot, approve or send back with a reason. Keyboard: A approves, R sends back,
 * J/K or arrows move. Editors can also mint posts here from finished renders.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useRouteContext } from '@tanstack/react-router';
import { PLATFORM_RULES, Platform, roleAtLeast } from '@studio/contracts';
import { AutoPill } from '@/components/auto-marker';
import { TopBar } from '@/components/shell';
import { queryClient, trpc } from '@/lib/trpc';

type Tab = 'awaiting_approval' | 'draft' | 'scheduled';
const TABS: { key: Tab; label: string }[] = [
  { key: 'awaiting_approval', label: 'Awaiting approval' },
  { key: 'draft', label: 'Sent back' },
  { key: 'scheduled', label: 'Approved' },
];

export function ApprovePage() {
  const { me } = useRouteContext({ from: '/_app' });
  const [tab, setTab] = useState<Tab>('awaiting_approval');
  const [index, setIndex] = useState(0);
  const [caption, setCaption] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [asking, setAsking] = useState(false);
  const [platforms, setPlatforms] = useState<Set<Platform>>(new Set(['tiktok', 'instagram']));

  const queue = useQuery(trpc.posts.queue.queryOptions({ status: tab }));
  const canEdit = roleAtLeast(me.active?.role ?? 'reviewer', 'editor');
  const renders = useQuery(trpc.posts.readyRenders.queryOptions(undefined, { enabled: canEdit }));

  const refresh = async () => {
    setCaption(null);
    setReason('');
    setAsking(false);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.posts.queue.queryKey({ status: tab }) }),
      canEdit ? queryClient.invalidateQueries({ queryKey: trpc.posts.readyRenders.queryKey() }) : Promise.resolve(),
    ]);
  };
  const create = useMutation(trpc.posts.create.mutationOptions({ onSuccess: refresh }));
  const update = useMutation(trpc.posts.update.mutationOptions({ onSuccess: refresh }));
  const approve = useMutation(trpc.posts.approve.mutationOptions({ onSuccess: refresh }));
  const sendBack = useMutation(trpc.posts.sendBack.mutationOptions({ onSuccess: refresh }));
  const regenerate = useMutation(trpc.posts.regenerate.mutationOptions({ onSuccess: refresh }));

  const posts = queue.data?.posts ?? [];
  const post = posts[Math.min(index, Math.max(0, posts.length - 1))] ?? null;
  const counts = queue.data?.counts ?? {};

  useEffect(() => setIndex(0), [tab]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (!post) return;
      if (e.key === 'a' && tab === 'awaiting_approval') approve.mutate({ postId: post.id });
      else if (e.key === 'r' && tab === 'awaiting_approval') setAsking(true);
      else if (e.key === 'j' || e.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, posts.length - 1));
      else if (e.key === 'k' || e.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const saveCaption = () => {
    if (post && caption !== null && caption !== post.caption) update.mutate({ postId: post.id, caption });
    else setCaption(null);
  };

  return (
    <>
      <TopBar title="Approve">
        {posts.length > 0 && <span className="sub crumb">{index + 1} of {posts.length}</span>}
        <div className="seg upload-tabs">
          {TABS.map((t) => (
            <button key={t.key} type="button" className={t.key === tab ? 'on' : ''} onClick={() => setTab(t.key)}>
              {t.label} · {counts[t.key === 'scheduled' ? 'scheduled' : t.key] ?? 0}
            </button>
          ))}
        </div>
        <div className="right">
          <AutoPill>Auto · caption and slot proposed</AutoPill>
        </div>
      </TopBar>
      <section className="content approve-content">
        {canEdit && (renders.data?.length ?? 0) > 0 && (
          <div className="card pad approve-new">
            <div className="row between">
              <div className="h2">Finished renders without a post</div>
              <div className="row approve-platforms">
                {Platform.options.map((p) => (
                  <button key={p} type="button" className={`chip${platforms.has(p) ? ' acc' : ''}`}
                    onClick={() => setPlatforms((s) => { const n = new Set(s); if (n.has(p)) n.delete(p); else n.add(p); return n; })}>
                    {PLATFORM_RULES[p].label}
                  </button>
                ))}
              </div>
            </div>
            <div className="row approve-renders">
              {renders.data!.map((r) => (
                <div key={r.id} className="approve-render">
                  <video src={r.videoUrl} className="approve-render-video" muted playsInline preload="metadata" />
                  <span className="sub approve-render-brief">{r.brief.slice(0, 44) || r.kind}</span>
                  <button type="button" className="btn primary" disabled={create.isPending || platforms.size === 0}
                    onClick={() => create.mutate({ renderId: r.id, platforms: [...platforms] })}>
                    {create.isPending ? 'Writing captions…' : `Make ${platforms.size} post${platforms.size === 1 ? '' : 's'}`}
                  </button>
                </div>
              ))}
            </div>
            {create.data && create.data.failed.length > 0 && (
              <div className="row">{create.data.failed.map((f) => <span key={f.platform} className="chip warn">{f.platform}: {f.reason}</span>)}</div>
            )}
            {create.isError && <div className="error" role="alert">{create.error.message}</div>}
          </div>
        )}

        {queue.isPending ? (
          <div className="empty"><div className="spinner" aria-label="Loading" /></div>
        ) : !post ? (
          <div className="empty">Nothing {tab === 'draft' ? 'sent back' : tab === 'scheduled' ? 'approved yet' : 'awaiting approval'}. {canEdit ? 'Render a draft in Compose, then make posts above.' : 'The studio will send posts here for review.'}</div>
        ) : (
          <div className="approve-body">
            <div className="card approve-phone">
              <div className="preview-frame f-9-16">
                <video key={post.id} className="preview-video" src={post.videoUrl ?? undefined} controls playsInline preload="metadata" />
              </div>
              <div className="approve-feed">
                <b>{PLATFORM_RULES[post.platform as Platform]?.label ?? post.platform}</b>
                <span className="approve-feed-caption">{post.caption}</span>
                <span className="sub">{post.hashtags.map((h) => `#${h}`).join(' ')}</span>
              </div>
            </div>
            <div className="approve-panel">
              <div className="card pad approve-section">
                <div className="row between">
                  <div className="h2">Caption · {PLATFORM_RULES[post.platform as Platform]?.label ?? post.platform}<AutoPill>auto</AutoPill></div>
                  <button type="button" className="btn quiet" disabled={regenerate.isPending} onClick={() => regenerate.mutate({ postId: post.id, ask: null })}>
                    {regenerate.isPending ? 'Writing…' : 'Regenerate'}
                  </button>
                </div>
                <textarea className="approve-caption" value={caption ?? post.caption} rows={4} aria-label="Caption"
                  onChange={(e) => setCaption(e.target.value)} onBlur={saveCaption} />
                <div className="row approve-tags">
                  {post.hashtags.map((h) => <span key={h} className="tg">#{h}</span>)}
                </div>
                {post.belowHook && <p className="sub approve-hook">Below the hook: {post.belowHook}</p>}
              </div>
              <div className="card pad approve-section">
                <div className="row between">
                  <span className="label">Slot</span>
                  <span className="chip acc">{post.slotAt ? new Date(post.slotAt).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'unbooked'}</span>
                </div>
                <span className="sub approve-slot-note">Booked from the brand&apos;s daily shape. Metricool connection is deferred; scheduled posts wait here until it lands.</span>
              </div>
              {tab === 'awaiting_approval' && (
                <div className="card pad approve-section">
                  {!asking ? (
                    <div className="row approve-actions">
                      <button type="button" className="btn primary approve-btn" disabled={approve.isPending} onClick={() => approve.mutate({ postId: post.id })}>
                        Approve <span className="kbd">A</span>
                      </button>
                      <button type="button" className="btn" onClick={() => setAsking(true)}>Send back <span className="kbd">R</span></button>
                      <span className="sub approve-nav-hint"><span className="kbd">J</span>/<span className="kbd">K</span> next and previous</span>
                    </div>
                  ) : (
                    <div className="row approve-actions">
                      <label className="field approve-reason"><input autoFocus value={reason} placeholder="What should change?" aria-label="Send back reason" onChange={(e) => setReason(e.target.value)} /></label>
                      <button type="button" className="btn primary" disabled={reason.trim().length < 2 || sendBack.isPending}
                        onClick={() => sendBack.mutate({ postId: post.id, reason: reason.trim() })}>Send back</button>
                      <button type="button" className="btn quiet" onClick={() => setAsking(false)}>Cancel</button>
                    </div>
                  )}
                  {(approve.isError || sendBack.isError) && <div className="error" role="alert">{approve.error?.message ?? sendBack.error?.message}</div>}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
